// apps/vendor/src/pages/auth/ForgotPasswordPage.tsx
//
// Phase 1 Task 47 — Forgot password screen (vendor portal).
//
// Per CONTEXT + SPECS / AC-07:
//   - Email input → POST /api/v1/auth/forgot-password
//   - ALWAYS show generic success message regardless of API outcome
//     (defends against email-enumeration).
//   - Recaptcha token is enforced server-side (route middleware); FE sends an
//     empty body field so the body parser does not 400.
//
// Implementation notes
//   - react-hook-form + zod resolver — keeps inline validation consistent with
//     the other auth screens in this wave (Login/Register/Reset).
//   - apiClient() from @printway/shared/api — typed openapi-fetch wrapper.
//     Cast pattern matches `packages/ui/src/hooks/useAuth.ts` so we don't
//     wedge against the still-stub `paths` types.

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';

// ---------------------------------------------------------------------------
// Schema — mirrors apps/api `PostAuthForgotPasswordRequest`.
// ---------------------------------------------------------------------------

const ForgotPasswordSchema = z.object({
  email: z.string().email(i18nT('auth:forgotPassword.validation.emailInvalid')).max(255),
});
type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordInput): Promise<void> => {
    // Email-enumeration defence: ignore network errors entirely so
    // the UI never leaks miss/hit signal. The handler always returns 200 on
    // happy + miss paths; only outright network failure could throw here.
    try {
      const client = apiClient();
      await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/forgot-password', {
        body: { email: data.email, recaptchaToken: '' },
      });
    } catch {
      // Swallow — generic success message regardless.
    } finally {
      setSubmittedEmail(data.email);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <AuthSplitLayout variant="marketing">
      <div className="flex flex-1 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('forgotPassword.successTitle')}</CardTitle>
            <CardDescription>
              {t('forgotPassword.successBodyPrefix')}{' '}
              <span className="font-medium text-pw-text">{submittedEmail}</span>{' '}
              {t('forgotPassword.successBodySuffix')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t('forgotPassword.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout variant="marketing">
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('forgotPassword.title')}</CardTitle>
          <CardDescription>
            {t('forgotPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* noValidate — để zod hiển thị lỗi inline thay vì tooltip native
              của trình duyệt (UAT #2352: type="email" chặn submit im lặng). */}
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="email">{t('forgotPassword.labelEmail')}</FormLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="ban@congty.com"
                {...register('email')}
              />
              {errors.email ? (
                <FormMessage>{errors.email.message}</FormMessage>
              ) : null}
            </FormField>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
            </Button>

            <p className="text-center text-sm">
              <Link to="/login" className="text-pw-text-muted underline">
                {t('forgotPassword.backToLogin')}
              </Link>
            </p>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}

export default ForgotPasswordPage;
